import moment from "moment";
import { useEffect, useState } from "react";

import { getSupaClient } from "../../lib/supabase";

const defaultTimeFormat = "YYYY-MM-DDTHH:mm:ssZ";

/**
 * This hook fetches Discord or Slack member metrics between two dates
 *
 * @param startTime - beginning of time period
 * @param  endTime - end of time period
 * @param  teamId - guild id for Dicord, team id for Slack
 * @param  platform - "slack" for Slack, "discord" for Discord
 * @returns An object --> { activeMemberMetrics: array of active members metrics, loading: "true" when loading data, "false" when done}
 */
const useActiveMemberMetrics = (startTime, endTime, teamId, platform) => {
  const defaultValues = {
    currentActive: "-",
    previousActive: "-",
    loading: true,
  };

  const [activeMemberMetrics, setActiveMemberMetrics] = useState(defaultValues);
  const [loading, setLoading] = useState(true);
  const supabase = getSupaClient();

  /**
   * Fetches Discord or Slack active member counts for a given period of time
   */
  const loadMembersMetrics = async () => {
    setLoading(true);
    setActiveMemberMetrics(defaultValues);

    const teamOrGuild = {
      slack: "team_id",
      discord: "guild_id",
    };

    const params = {
      end_time: endTime,
      start_time: startTime,
      [teamOrGuild[platform]]: teamId,
    };

    const dayDiff = moment(endTime).diff(moment(startTime), "days");

    const previousParams = {
      end_time: moment(endTime)
        .subtract(dayDiff, "day")
        .format(defaultTimeFormat),
      start_time: moment(startTime)
        .subtract(dayDiff, "day")
        .format(defaultTimeFormat),
      [teamOrGuild[platform]]: teamId,
    };

    let currentActive;
    let previousActive;
    if (platform === "slack") {
      const [currentActiveData, previousActiveData] = await Promise.all([
        supabase.rpc("active_users_count", params),
        supabase.rpc("active_users_count", previousParams),
      ]);
      currentActive = currentActiveData;
      previousActive = previousActiveData;
    } else if (platform === "discord") {
      const [currentActiveData, previousActiveData] = await Promise.all([
        supabase.rpc("discord_active_members_count", params),
        supabase.rpc("discord_active_members_count", previousParams),
      ]);
      currentActive = currentActiveData;
      previousActive = previousActiveData;
    }

    setActiveMemberMetrics({
      currentActive: currentActive.data[0]
        ? currentActive.data[0].total_count
        : "-",
      previousActive: previousActive.data[0]
        ? previousActive.data[0].total_count
        : "-",
      activePercent:
        currentActive.data[0] && previousActive.data[0]
          ? (
              ((currentActive.data[0].total_count -
                previousActive.data[0].total_count) /
                previousActive.data[0].total_count) *
              100
            ).toFixed(1)
          : "-",
      loading: false,
    });
    setLoading(false);
  };

  useEffect(() => {
    loadMembersMetrics();
  }, [startTime, endTime]);

  return { activeMemberMetrics, loading };
};

export default useActiveMemberMetrics;
